import { useCallback, useId, useState, type FocusEvent, type PointerEvent } from 'react';
import { Tooltip } from './Tooltip';
import styles from './useHoverTip.module.css';

/** Below this distance from the window's top, a bubble opens under its control instead. */
const ROOM_ABOVE = 48;

type Tip = { label: string; box: DOMRect; above: boolean };

/**
 * A tooltip for an icon-only control: the system's `Tooltip` over the control
 * the moment it is hovered or focused. The browser's own `title` tooltip,
 * which these controls used, waits a second or more before it shows and
 * cannot be styled — "Ask again in Expert mode" read as broken (2026-10-28).
 *
 * Spread `trigger(label)` on a wrapper around the control — `Button` takes no
 * pointer or focus handlers, and focus bubbles up to the wrapper — or `group`
 * on a row of buttons, and render `bubble` beside it. One bubble per hook, so
 * a row of controls shares one.
 * It is fixed to the window over the control, as `TableCell`'s reveal is, so
 * an ancestor's `overflow` cannot clip it; leaving, blurring, pressing or
 * Escape closes it. The control keeps its own `aria-label`, which already
 * names what the bubble says.
 */
export function useHoverTip() {
  const id = useId();
  const [tip, setTip] = useState<Tip | null>(null);
  const hide = useCallback(() => setTip(null), []);

  const show = (label: string, element: HTMLElement) => {
    const box = element.getBoundingClientRect();
    setTip({ label, box, above: box.top > ROOM_ABOVE });
  };

  const trigger = (label: string) => ({
    onPointerEnter: (event: PointerEvent<HTMLElement>) => show(label, event.currentTarget),
    onPointerLeave: hide,
    onFocus: (event: FocusEvent<HTMLElement>) => show(label, event.currentTarget),
    onBlur: hide,
    onPointerDown: hide,
    onKeyDown: (event: { key: string }) => {
      if (event.key === 'Escape') hide();
    },
  });

  /*
   * For a group of controls, spread on the group instead: the bubble names
   * whichever labelled button the pointer or focus is on, by its
   * `aria-label`, and the group's markup stays exactly as it was.
   */
  const showButton = (target: EventTarget) => {
    const button = target instanceof Element ? target.closest('button[aria-label]') : null;
    const label = button?.getAttribute('aria-label');
    if (!button || !label) return hide();
    if (tip?.label !== label) show(label, button as HTMLElement);
  };
  const group = {
    onPointerOver: (event: PointerEvent<HTMLElement>) => showButton(event.target),
    onPointerLeave: hide,
    onFocus: (event: FocusEvent<HTMLElement>) => showButton(event.target),
    onBlur: hide,
    onPointerDown: hide,
  };

  const bubble = tip && (
    <span
      className={styles.anchor}
      style={{
        left: tip.box.left,
        top: tip.box.top,
        width: tip.box.width,
        height: tip.box.height,
      }}
    >
      <Tooltip
        id={id}
        arrow={tip.above ? 'bottomCenter' : 'topCenter'}
        className={`${styles.bubble} ${tip.above ? styles.above : styles.below}`}
      >
        {tip.label}
      </Tooltip>
    </span>
  );

  return { trigger, group, hide, bubble };
}
